import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronRight, Play } from 'lucide-react';
import Testimonials from '../components/Testimonials';
import Newsletter from '../components/Newsletter';

// Animated counter component
const AnimatedNumber = ({ value, suffix = '' }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return <span>{count.toLocaleString()}{suffix}</span>;
};

// Typewriter effect component
const TypewriterText = ({ texts, className }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  useEffect(() => {
    const currentText = texts[currentIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentText.length) {
          setDisplayText(currentText.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(currentText.slice(0, displayText.length - 1));
        } else {
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % texts.length);
        }
      }
    }, isDeleting ? 50 : 100);
    
    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentIndex, texts]);
  
  return (
    <span className={className}>
      [{displayText}<span className="animate-pulse">|</span>]
    </span>
  );
};

// Animated grid lines
const GridLines = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Vertical lines */}
    {[...Array(12)].map((_, i) => (
      <motion.div
        key={`v-${i}`}
        className="absolute top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gold/10 to-transparent"
        style={{ left: `${(i + 1) * 8.33}%` }}
        initial={{ opacity: 0, scaleY: 0 }}
        animate={{ opacity: 1, scaleY: 1 }}
        transition={{ delay: i * 0.05, duration: 0.8 }}
      />
    ))}
    {/* Horizontal lines */}
    {[...Array(8)].map((_, i) => (
      <motion.div
        key={`h-${i}`}
        className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/10 to-transparent"
        style={{ top: `${(i + 1) * 12.5}%` }}
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ delay: 0.5 + i * 0.05, duration: 0.8 }}
      />
    ))}
  </div>
);

// Floating particles
const Particles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1 h-1 bg-gold/30 rounded-full"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
        animate={{
          y: [0, -30, 0],
          opacity: [0.3, 0.8, 0.3],
        }}
        transition={{
          duration: 3 + Math.random() * 2,
          repeat: Infinity,
          delay: Math.random() * 2,
        }}
      />
    ))}
  </div>
);

export default function HomePage() {
  const [hoveredProduct, setHoveredProduct] = useState(null);

  const products = [
    {
      id: 'geobar-b',
      name: 'GEObar B',
      type: 'BFRP',
      spec: '164,000 psi',
      description: 'Basalt fiber composite rebar'
    },
    {
      id: 'geobar-fg',
      name: 'GEObar FG',
      type: 'GFRP',
      spec: '160,000 psi',
      description: 'Glass fiber composite rebar'
    },
    {
      id: 'geofabric',
      name: 'GEOfabric',
      type: 'GEOGRID',
      spec: '1,000+ MPa',
      description: 'Basalt fiber geogrid mesh'
    },
    {
      id: 'chopped-fiber',
      name: 'GEOfiber',
      type: 'ADDITIVE',
      spec: 'Crack-Free',
      description: 'Chopped basalt concrete fiber'
    }
  ];

  return (
    <div className="bg-charcoal min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Animated background elements */}
        <GridLines />
        <Particles />
        
        {/* Scan line effect */}
        <div className="scan-line" />
        
        {/* Corner markers */}
        <div className="absolute top-8 left-8 w-16 h-16 border-l border-t border-gold/30" />
        <div className="absolute top-8 right-8 w-16 h-16 border-r border-t border-gold/30" />
        <div className="absolute bottom-8 left-8 w-16 h-16 border-l border-b border-gold/30" />
        <div className="absolute bottom-8 right-8 w-16 h-16 border-r border-b border-gold/30" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 pt-40 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Tech label */}
              <motion.div 
                className="flex items-center gap-4 mb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <span className="tech-label">//COMPOSITE_REINFORCEMENT</span>
                <div className="h-px w-24 bg-gradient-to-r from-gold/50 to-transparent" />
              </motion.div>

              {/* Main headline - clean, no glitch */}
              <h1 className="mb-6">
                <motion.span 
                  className="block heading-display text-5xl md:text-6xl lg:text-7xl text-stone leading-none"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  Engineering
                </motion.span>
                <motion.span 
                  className="block heading-display text-5xl md:text-6xl lg:text-7xl text-gold leading-none mt-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  Superiority
                </motion.span>
                <motion.div
                  className="mt-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  <TypewriterText 
                    texts={['Composites', 'Innovation', 'Performance', 'Durability']}
                    className="heading-display text-3xl md:text-4xl text-stone/50"
                  />
                </motion.div>
              </h1>

              {/* Description */}
              <motion.p 
                className="text-stone/50 max-w-xl mb-12 text-lg leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                Next-generation composite reinforcement materials. Corrosion-immune. 
                Engineered for infrastructure that outlasts steel.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div 
                className="flex flex-wrap gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
              >
                <Link 
                  to="/products" 
                  className="btn btn-primary group"
                  data-testid="hero-view-products-btn"
                >
                  <span>View Products</span>
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/contact" 
                  className="btn btn-outline"
                  data-testid="hero-quote-btn"
                >
                  Request Quote
                </Link>
              </motion.div>

              {/* Key stats - simple horizontal row */}
              <motion.div 
                className="mt-16 pt-8 border-t border-border-dark flex flex-wrap gap-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
              >
                <div>
                  <span className="heading-display text-3xl text-gold">2×</span>
                  <span className="block text-stone/40 text-xs mt-1 font-mono">Tensile Strength</span>
                </div>
                <div>
                  <span className="heading-display text-3xl text-gold">100+</span>
                  <span className="block text-stone/40 text-xs mt-1 font-mono">Year Service Life</span>
                </div>
                <div>
                  <span className="heading-display text-3xl text-gold">75%</span>
                  <span className="block text-stone/40 text-xs mt-1 font-mono">Weight Reduction</span>
                </div>
                <div>
                  <span className="heading-display text-3xl text-gold">0%</span>
                  <span className="block text-stone/40 text-xs mt-1 font-mono">Corrosion</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Right side - Abstract Infrastructure Visual */}
            <motion.div
              className="relative hidden lg:block"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 1 }}
            >
              <div className="relative w-full aspect-square max-w-lg mx-auto">
                {/* Outer rotating ring */}
                <motion.div 
                  className="absolute inset-0 border border-gold/20 rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                >
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-gold rounded-full" />
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gold/50 rounded-full" />
                </motion.div>

                {/* Middle counter-rotating ring */}
                <motion.div 
                  className="absolute inset-8 border border-gold/30"
                  style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}
                  animate={{ rotate: -360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />

                {/* Inner hexagon structure */}
                <div className="absolute inset-16 border border-gold/20" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}>
                  <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent" />
                </div>

                {/* Central core */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div 
                    className="relative"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  >
                    {/* Rebar cross-section visualization */}
                    <div className="w-32 h-32 relative">
                      {/* Outer fiber ring */}
                      <div className="absolute inset-0 rounded-full border-4 border-gold/40" />
                      {/* Inner core */}
                      <div className="absolute inset-4 rounded-full bg-gradient-to-br from-gold/30 to-gold/10" />
                      {/* Fiber strands */}
                      {[...Array(8)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute top-1/2 left-1/2 w-1 h-12 bg-gradient-to-b from-gold to-transparent origin-bottom"
                          style={{ 
                            transform: `translate(-50%, -100%) rotate(${i * 45}deg)`,
                          }}
                          animate={{ opacity: [0.3, 0.8, 0.3] }}
                          transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
                        />
                      ))}
                      {/* Center dot */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <motion.div 
                          className="w-4 h-4 bg-gold rounded-full"
                          animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Floating data points */}
                <motion.div 
                  className="absolute top-8 right-8 text-right"
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <span className="text-mono text-xs text-gold/60 block">BFRP_FIBER</span>
                  <span className="text-mono text-xs text-stone/40">1,000+ MPa</span>
                </motion.div>

                <motion.div 
                  className="absolute bottom-8 left-8"
                  animate={{ y: [0, 5, 0] }}
                  transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                >
                  <span className="text-mono text-xs text-gold/60 block">MATRIX</span>
                  <span className="text-mono text-xs text-stone/40">Epoxy Resin</span>
                </motion.div>

                <motion.div 
                  className="absolute top-1/2 -right-4 -translate-y-1/2"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <span className="text-mono text-xs text-gold/60 block">TENSILE</span>
                  <span className="text-mono text-xs text-stone/40">164K PSI</span>
                </motion.div>

                {/* Connection lines */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 400">
                  <motion.line
                    x1="320" y1="60" x2="250" y2="120"
                    stroke="rgba(212, 175, 55, 0.3)"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, delay: 1 }}
                  />
                  <motion.line
                    x1="80" y1="340" x2="150" y2="280"
                    stroke="rgba(212, 175, 55, 0.3)"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, delay: 1.2 }}
                  />
                  <motion.line
                    x1="380" y1="200" x2="280" y2="200"
                    stroke="rgba(212, 175, 55, 0.3)"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, delay: 1.4 }}
                  />
                </svg>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <span className="text-mono text-xs text-stone/30 tracking-widest">SCROLL</span>
          <motion.div 
            className="w-px h-12 bg-gradient-to-b from-gold/50 to-transparent"
            animate={{ scaleY: [1, 0.5, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.div>
      </section>

      {/* Trust Bar */}
      <section className="border-y border-border-dark py-4 bg-surface-dark/50" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {['ASTM D7205', 'ACI 440.1R', 'ISO 9001:2015', 'CE CERTIFIED'].map((item, index) => (
              <motion.span 
                key={index}
                className="text-mono text-stone/30 text-xs tracking-wider"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                {item}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* The Problem - Corrosion Section */}
      <section className="py-24 lg:py-32 bg-surface-dark/30 relative overflow-hidden" data-testid="problem-section">
        {/* Background accent */}
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/5 to-transparent pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="tech-label text-red-500/70">//THE_PROBLEM</span>
              <div className="h-px flex-1 bg-gradient-to-r from-red-500/30 to-transparent" />
            </div>
            <h2 className="heading-display text-4xl md:text-5xl text-stone">
              Steel Rebar <span className="text-red-500/80">Fails</span>
            </h2>
            <p className="text-stone/50 mt-4 max-w-2xl text-lg">
              $276 billion annually. That's the cost of corrosion to U.S. infrastructure. 
              Steel rebar is the weakest link in reinforced concrete.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Images */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="relative group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_composite-rebar/artifacts/je9vl65n_Screenshot%202026-03-04%20at%2011.25.28%E2%80%AFAM.png"
                  alt="Corroded steel rebar causing concrete failure"
                  className="w-full aspect-square object-cover border border-red-500/20 group-hover:border-red-500/40 transition-colors"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 to-transparent flex items-end p-4">
                  <span className="text-mono text-xs text-red-400">CORROSION_DAMAGE</span>
                </div>
              </div>
              <div className="relative group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_composite-rebar/artifacts/ylyjcfhr_Screenshot%202026-03-04%20at%2011.25.33%E2%80%AFAM.png"
                  alt="Rusted rebar structural failure"
                  className="w-full aspect-square object-cover border border-red-500/20 group-hover:border-red-500/40 transition-colors"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 to-transparent flex items-end p-4">
                  <span className="text-mono text-xs text-red-400">STRUCTURAL_FAILURE</span>
                </div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="space-y-6">
                <div className="p-6 border border-red-500/20 bg-charcoal/50">
                  <div className="flex items-baseline gap-4 mb-2">
                    <span className="heading-display text-4xl text-red-500/80">15-25</span>
                    <span className="text-stone/40 text-sm">YEARS</span>
                  </div>
                  <p className="text-stone/60 text-sm">
                    Average lifespan of steel rebar in marine or chloride-exposed environments before critical failure
                  </p>
                </div>

                <div className="p-6 border border-red-500/20 bg-charcoal/50">
                  <div className="flex items-baseline gap-4 mb-2">
                    <span className="heading-display text-4xl text-red-500/80">$276B</span>
                    <span className="text-stone/40 text-sm">ANNUAL COST</span>
                  </div>
                  <p className="text-stone/60 text-sm">
                    U.S. infrastructure corrosion costs — bridges, highways, parking structures, marine facilities
                  </p>
                </div>

                <div className="p-6 border border-red-500/20 bg-charcoal/50">
                  <div className="flex items-baseline gap-4 mb-2">
                    <span className="heading-display text-4xl text-red-500/80">40%</span>
                    <span className="text-stone/40 text-sm">DEFICIENT</span>
                  </div>
                  <p className="text-stone/60 text-sm">
                    Of U.S. bridges rated structurally deficient or functionally obsolete due to rebar corrosion
                  </p>
                </div>
              </div>

              <motion.div 
                className="mt-8 p-6 border border-gold/30 bg-gold/5"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
              >
                <p className="text-gold font-mono text-sm mb-2">THE SOLUTION:</p>
                <p className="text-stone/70">
                  Composite FRP rebar is <span className="text-gold">immune to corrosion</span>. 
                  Zero rust. Zero expansion. Zero concrete spalling. 100+ year service life.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-24 lg:py-32 relative" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Section header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="tech-label">//PRODUCT_CATALOG</span>
              <div className="h-px flex-1 bg-gradient-to-r from-gold/30 to-transparent" />
            </div>
            <h2 className="heading-display text-4xl md:text-5xl text-stone">
              The GEO <span className="text-gold">Family</span>
            </h2>
          </motion.div>

          {/* Products grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => setHoveredProduct(product.id)}
                onMouseLeave={() => setHoveredProduct(null)}
              >
                <Link 
                  to={`/products/${product.id}`}
                  className="block h-full"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className={`h-full p-6 border transition-all duration-300 ${
                    hoveredProduct === product.id 
                      ? 'border-gold bg-gold/5' 
                      : 'border-border-dark bg-surface-dark/30'
                  }`}>
                    {/* Type badge */}
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-mono text-xs text-gold/70 px-2 py-1 border border-gold/30">
                        {product.type}
                      </span>
                      <ChevronRight 
                        size={16} 
                        className={`text-gold transition-transform ${
                          hoveredProduct === product.id ? 'translate-x-1' : ''
                        }`} 
                      />
                    </div>

                    {/* Product visual */}
                    <div className="h-24 mb-6 flex items-center justify-center relative overflow-hidden">
                      <motion.div
                        className={`absolute inset-0 bg-gradient-to-br from-gold/10 to-transparent transition-opacity ${
                          hoveredProduct === product.id ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                      {product.type === 'BFRP' || product.type === 'GFRP' ? (
                        <div className="w-24 h-1.5 bg-gold/40" />
                      ) : product.type === 'GEOGRID' ? (
                        <div className="grid grid-cols-4 gap-1">
                          {[...Array(16)].map((_, i) => (
                            <div key={i} className="w-4 h-4 border border-gold/30" />
                          ))}
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          {[...Array(8)].map((_, i) => (
                            <div 
                              key={i} 
                              className="w-0.5 bg-gold/40" 
                              style={{ height: `${16 + Math.random() * 16}px` }} 
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Product info */}
                    <h3 className="heading-section text-xl text-stone mb-2">{product.name}</h3>
                    <p className="text-mono text-xs text-stone/40 mb-4">{product.description}</p>
                    
                    {/* Spec highlight */}
                    <div className="pt-4 border-t border-border-dark">
                      <span className="text-mono text-xs text-gold">{product.spec}</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 lg:py-32 border-y border-border-dark bg-surface-dark/30" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '2×', label: 'Tensile strength vs steel' },
              { value: '100+', label: 'Year service life' },
              { value: '75%', label: 'Weight reduction' },
              { value: '0%', label: 'Corrosion rate' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <span className="heading-display text-4xl md:text-5xl text-gold block mb-2">
                  {stat.value}
                </span>
                <span className="text-mono text-xs text-stone/40 uppercase tracking-wider">
                  {stat.label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 lg:py-32" data-testid="specs-preview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="tech-label">//PERFORMANCE_DATA</span>
              <div className="h-px flex-1 bg-gradient-to-r from-gold/30 to-transparent" />
            </div>
            <h2 className="heading-display text-4xl md:text-5xl text-stone">
              Technical <span className="text-gold">Comparison</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="overflow-x-auto"
          >
            <table className="spec-table" data-testid="comparison-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>GEObar B</th>
                  <th>GEObar FG</th>
                  <th>Grade 60 Steel</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Tensile Strength</td>
                  <td className="text-gold">145,000–164,000 psi</td>
                  <td>110,000–160,000 psi</td>
                  <td className="text-stone/40">90,000 psi</td>
                </tr>
                <tr>
                  <td>Weight</td>
                  <td className="text-gold">~0.13 lb/ft</td>
                  <td>~0.14 lb/ft</td>
                  <td className="text-stone/40">0.668 lb/ft</td>
                </tr>
                <tr>
                  <td>Corrosion</td>
                  <td className="text-gold">IMMUNE</td>
                  <td className="text-gold">IMMUNE</td>
                  <td className="text-stone/40">Vulnerable</td>
                </tr>
                <tr>
                  <td>Service Life</td>
                  <td className="text-gold">100+ years</td>
                  <td className="text-gold">75+ years</td>
                  <td className="text-stone/40">15–25 years</td>
                </tr>
              </tbody>
            </table>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-8 flex justify-end"
          >
            <Link 
              to="/specifications" 
              className="inline-flex items-center text-gold text-mono text-sm animated-underline"
              data-testid="view-full-specs"
            >
              View Full Specifications <ArrowRight size={14} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 border-t border-border-dark relative overflow-hidden" data-testid="cta-section">
        <GridLines />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="tech-label block mb-6">//INITIATE_CONTACT</span>
            <h2 className="heading-display text-4xl md:text-5xl lg:text-6xl text-stone mb-6">
              Ready to <span className="text-gold">Specify</span>?
            </h2>
            <p className="text-stone/50 max-w-xl mx-auto mb-10 text-mono text-sm">
              Request technical data sheets, sample kits, or a project quote. 
              Response within one business day.
            </p>
            <Link 
              to="/contact" 
              className="btn btn-primary"
              data-testid="cta-contact-btn"
            >
              Contact Us
              <ArrowRight size={16} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 lg:py-32 bg-surface-dark/30" data-testid="video-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-4 mb-4">
              <Play size={20} className="text-gold" />
              <span className="tech-label">//SEE_THE_DIFFERENCE</span>
            </div>
            <h2 className="heading-display text-4xl md:text-5xl text-stone">
              Composite <span className="text-gold">Innovation</span>
            </h2>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative aspect-video border border-border-dark bg-charcoal overflow-hidden"
          >
            <video 
              controls 
              className="w-full h-full object-cover"
              poster="https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1200&q=80"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_476a6dd3-db49-4ee1-922f-dcd7c2c3de86/artifacts/nnf6o1ff_Generated%20File%20March%2004%2C%202026%20-%2012_05PM.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* Newsletter Section */}
      <Newsletter />
    </div>
  );
}
